import type { AstroLocale } from '@/schemas/components/shared/i18n';
import type { DropDownItem } from '../elements/SelectDropdown';

export interface LocationDropdownItem
  extends Omit<
    Required<DropDownItem>,
    'valueOverride' | 'phoneCountryCode' | 'flag'
  > {
  twoLetterCode: string;
  valueOverride?: string;
  dial_code?: string;
}

/**
 * Gets prioritized countries for each language
 */
const getPriorityCountries = (lang: AstroLocale): string[] => {
  switch (lang) {
    case 'de':
      return ['Austria', 'Switzerland', 'Germany'];
    case 'en':
      return ['United States', 'Canada', 'United Kingdom'];
    case 'es':
      return ['Spain'];
    case 'fr':
      return ['France', 'Canada'];
    case 'nl':
      return ['Netherlands'];
    case 'zh':
      return ['China', 'Macau', 'Taiwan', 'Hong Kong'];
    default:
      return ['United States', 'Canada', 'United Kingdom'];
  }
};

export const getOptions = (
  options: LocationDropdownItem[],
  lang: AstroLocale,
) => {
  const priorityCountries = getPriorityCountries(lang);

  return [...options].sort(
    (a: LocationDropdownItem, b: LocationDropdownItem) => {
      // Get priority indices (or -1 if not prioritized)
      const aPriority = priorityCountries.indexOf(a[lang]);
      const bPriority = priorityCountries.indexOf(b[lang]);

      // Case 1: Both countries are prioritized
      if (aPriority !== -1 && bPriority !== -1) {
        return aPriority - bPriority;
      }

      // Case 2: Only country A is prioritized
      if (aPriority !== -1) {
        return -1;
      }

      // Case 3: Only country B is prioritized
      if (bPriority !== -1) {
        return 1;
      }

      // Case 4: Neither country is prioritized, use alphabetical sorting
      return a[lang].localeCompare(b[lang], lang);
    },
  );
};
